import { createContext } from 'react';
import { decorate, observable, action, toJS, computed } from 'mobx';
import uuid from 'react-uuid';

import api from '../api';
import * as fn from '../utilities/_functions';

export class CommunicationTemplateUpdate {
    id = null;
    data = null;
    selectedLanguage = null;
    selectedPartialVariableId = null;
    selectedPartialId = null;
    isSaving = false;
    isLoading = false;
    isReady = false;
    hasUnsavedChanges = false;

    cancelCommunicationTemplateGet = null;
    cancelCommunicationTemplateUpdate = null;
    cancelCommunicationTemplatePartialUpdate = null;

    load = id => {
        const that = this;
        this.clear();
        this.id = id;

        return new Promise((resolve) => {
            this.refresh()
                .finally(() => {
                    that.isReady = true;
                    resolve();
                });
        })
    }

    refresh = () => {
        const that = this;
        this.isLoading = true;

        return new Promise((resolve, reject) => {
            api.CommunicationTemplates.get(
                that.id,
                (c) => { that.cancelCommunicationTemplateGet = c }
            )
                .then(({ data }) => {
                    that.data = data;
                    that.data.subject = !!that.data.subject ? that.data.subject : that.data.defaultSubject;
                    that.data.preview = !!that.data.preview ? that.data.preview : that.data.defaultPreview;
                    that.data.body = !!that.data.body ? that.data.body : that.data.defaultBody;

                    if (that.data.partialKeyVariables && that.data.partialKeyVariables.length > 0 && !that.selectedPartialVariableId) {
                        that.selectedPartialVariableId = that.data.partialKeyVariables[0].id;
                    }
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isLoading = false;
                })
        })
    }

    save = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.hasUnsavedChanges) {
                let option = toJS(that.data);

                option.subject = option.subject === option.defaultSubject ? null : option.subject;
                option.preview = option.preview === option.defaultPreview ? null : option.preview;
                option.body = option.body === option.defaultBody ? null : option.body;

                api.CommunicationTemplates.update(
                    that.id,
                    option,
                    (c) => { that.cancelCommunicationTemplateUpdate = c }
                )
                    .then(() => {
                        that.hasUnsavedChanges = false;
                        resolve(option);
                    })
                    .catch(() => {
                        reject();
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            } else {
                that.isSaving = false;
                resolve();
            }
        })
    }

    preview = () => {
        const that = this;
        return new Promise((resolve, reject) => {
            const option = {
                selectedPartialId: that.selectedPartial ? that.selectedPartial.id : null
            };
            api.CommunicationTemplates.preview(that.id, option)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch(() => {
                    reject();
                })
        })
    }

    addLanguageTemplate = (language) => {
        if (this.data) {
            if (!this.data.communicationLanguageTemplates || !Array.isArray(this.data.communicationLanguageTemplates)) {
                this.data.communicationLanguageTemplates = [];
            }

            const index = this.data.communicationLanguageTemplates.findIndex(t => t.language === language);

            if (index < 0) {
                this.data.communicationLanguageTemplates.push({
                    id: fn.newId('clt'),
                    language: language,
                    subject: null,
                    preview: null,
                    body: null,
                    attachments: []
                });
            }
        }
    }

    addNewPartial = () => {
        if (this.selectedPartialVariableId) {
            this.selectedPartialId = uuid();
            this.data.partials.push({
                id: this.selectedPartialId,
                communicationTemplateId: this.id,
                key: this.selectedPartialVariableId,
                name: '',
                body: null,
                isNew: true,
            });
        }
    }

    clearNewPartial = () => {
        this.data.partials = this.data.partials.filter(p => !p.isNew);
    }

    savePartial = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.selectedPartial) {
                let option = toJS(that.selectedPartial);

                if (that.selectedPartial.isNew === true) {
                    api.CommunicationTemplatePartials.create(option, (c) => { that.cancelCommunicationTemplatePartialUpdate = c })
                        .then(({ data }) => {
                            const index = that.data.partials.findIndex(p => p.id === that.selectedPartialId);

                            if (index >= 0) {
                                that.data.partials[index].id = data.id;
                                that.data.partials[index].isNew = false;
                            }
                            resolve(data);
                        })
                        .catch(() => {
                            reject();
                        })
                        .finally(() => {
                            that.isSaving = false;
                        })
                }
                else {
                    api.CommunicationTemplatePartials.update(that.selectedPartialId, option, (c) => { that.cancelCommunicationTemplatePartialUpdate = c })
                        .then(() => {
                            resolve({ id: that.selectedPartialId });
                        })
                        .catch(() => {
                            reject();
                        })
                        .finally(() => {
                            that.isSaving = false;
                        })
                }
            } else {
                that.isSaving = false;
                resolve();
            }
        })
    }

    deletePartial = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.selectedPartialId) {
                api.CommunicationTemplatePartials.delete(that.selectedPartialId, (c) => { that.cancelCommunicationTemplatePartialUpdate = c })
                    .then(() => {
                        const index = that.data.partials.findIndex(p => p.id === that.selectedPartialId);

                        if (index >= 0) {
                            that.data.partials.splice(index, 1);
                        }
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    })
                    .finally(() => {
                        that.isSaving = false;
                    })
            }
        })
    }

    clear = () => {
        this.id = null;
        this.data = null;
        this.selectedLanguage = null;
        this.selectedPartialVariableId = null;
        this.selectedPartialId = null;
        this.isReady = false;
        this.isSaving = false;
        this.isLoading = false;
        this.hasUnsavedChanges = false;

        if (fn.isFunction(this.cancelCommunicationTemplateGet)) {
            this.cancelCommunicationTemplateGet();
            this.cancelCommunicationTemplateGet = null;
        }

        if (fn.isFunction(this.cancelCommunicationTemplateUpdate)) {
            this.cancelCommunicationTemplateUpdate();
            this.cancelCommunicationTemplateUpdate = null;
        }

        if (fn.isFunction(this.cancelCommunicationTemplatePartialUpdate)) {
            this.cancelCommunicationTemplatePartialUpdate();
            this.cancelCommunicationTemplatePartialUpdate = null;
        }
    }

    get isNotFound() {
        return this.isReady && !this.data;
    }

    get selectedTemplateIndex() {
        if (this.selectedLanguage) {
            if (this.data.communicationLanguageTemplates) {
                const index = this.data.communicationLanguageTemplates.findIndex(l => l.language === this.selectedLanguage);
                return index;
            }
        }

        return -1;
    }

    get selectedTemplate() {
        if (this.selectedLanguage) {
            if (this.data.communicationLanguageTemplates && this.selectedTemplateIndex !== -1) {
                return this.data.communicationLanguageTemplates[this.selectedTemplateIndex];
            }
            else {
                return null;
            }
        }

        return this.data;
    }

    get mentions() {
        if (this.data && this.data.variables && this.data.variables.length > 0) {
            const variables = toJS(this.data.variables).reduce((array, item) => {
                if (item.variables && item.variables.length > 0) {
                    for (let vi = 0; vi < item.variables.length; vi++) {
                        array.push({
                            key: item.variables[vi].defaultFormat.variable,
                            value: item.variables[vi].defaultFormat.variable,
                            display: item.variables[vi].name,
                            searchString: item.variables[vi].defaultFormat.searchString,
                            isPartial: !!item.variables[vi].isPartial,
                        })
                    }
                }
                return array;
            }, []);

            return variables;
        }

        return [];
    }

    get plainTextMentions() {
        if (this.data && this.data.variables && this.data.variables.length > 0) {
            const variables = toJS(this.data.variables).reduce((array, item) => {
                if (item.variables && item.variables.length > 0) {
                    for (let vi = 0; vi < item.variables.length; vi++) {
                        item.variables[vi].formats = item.variables[vi].formats.filter(f => f.isHtml === false);

                        if (item.variables[vi].defaultFormat.isHtml === true) {
                            item.variables[vi].defaultFormat = item.variables[vi].formats != null && item.variables[vi].formats.length > 0 ? item.variables[vi].formats[0] : null
                        }

                        if (item.variables[vi].defaultFormat) {
                            array.push({
                                key: item.variables[vi].defaultFormat.variable,
                                value: item.variables[vi].defaultFormat.variable,
                                display: item.variables[vi].name,
                                searchString: item.variables[vi].defaultFormat.searchString,
                                isPartial: !!item.variables[vi].isPartial,
                            })
                        }
                    }
                }
                return array;
            }, []);

            return variables;
        }

        return [];
    }

    get selectedPartialVariable() {
        if (this.selectedPartialVariableId && this.data.partialKeyVariables && this.data.partialKeyVariables.length > 0) {
            return this.data.partialKeyVariables.find(v => v.id === this.selectedPartialVariableId);
        }

        return null;
    }

    get selectedPartial() {
        if (this.selectedPartialId && this.data.partials && this.data.partials.length > 0) {
            return this.data.partials.find(p => p.id === this.selectedPartialId);
        }

        return null;
    }
}

decorate(CommunicationTemplateUpdate, {
    id: observable,
    data: observable.deep,
    selectedLanguage: observable,
    selectedPartialVariableId: observable,
    selectedPartialId: observable,
    isReady: observable,
    isSaving: observable,
    isLoading: observable,
    hasUnsavedChanges: observable,
    load: action,
    refresh: action,
    addLanguageTemplate: action,
    save: action,
    preview: action,
    addNewPartial: action,
    clearNewPartial: action,
    savePartial: action,
    deletePartial: action,
    clear: action,
    isNotFound: computed,
    selectedTemplateIndex: computed,
    selectedTemplate: computed,
    mentions: computed,
    plainTextMentions: computed,
    selectedPartialVariable: computed,
    selectedPartial: computed,
})

export default createContext(new CommunicationTemplateUpdate());